import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import type { Filters, MetricsFrameworkPeriod, MetricsFrameworkTabId } from '../types';
import { clearMetricsFrameworkTileCaches } from '../utils/tileCache';
import { getInitialPeriodFromSharedStorage } from '../utils/timePeriodPersistence';

const defaultMockApiFlag = process.env.REACT_APP_METRICS_FRAMEWORK_USE_MOCK === 'true';

type MetricsFrameworkContextType = {
  dealerId: string | null;
  dealerTitle: string | null;
  setDealerContext: (dealerId: string | null, dealerTitle: string | null) => void;
  period: MetricsFrameworkPeriod;
  setPeriod: (period: MetricsFrameworkPeriod) => void;
  activeView: MetricsFrameworkTabId | null;
  setActiveView: (view: MetricsFrameworkTabId | null) => void;
  filters: Filters | null;
  mockApiFlag: boolean;
  setMockApiFlag: (enabled: boolean) => void;
  dataRevision: number;
};

const MetricsFrameworkContext = createContext<MetricsFrameworkContextType | undefined>(
  undefined,
);

export const MetricsFrameworkProvider = ({ children }: { children: ReactNode }) => {
  const [dealerId, setDealerId] = useState<string | null>(null);
  const [dealerTitle, setDealerTitle] = useState<string | null>(null);
  const [period, setPeriod] = useState<MetricsFrameworkPeriod>(
    getInitialPeriodFromSharedStorage,
  );
  const [activeView, setActiveView] = useState<MetricsFrameworkTabId | null>(null);
  const [mockApiFlag, setMockApiFlagState] = useState(defaultMockApiFlag);
  const [dataRevision, setDataRevision] = useState(0);

  const setDealerContext = (id: string | null, title: string | null) => {
    setDealerId(id);
    setDealerTitle(title);
  };

  const setMockApiFlag = useCallback((enabled: boolean) => {
    setMockApiFlagState(enabled);
    clearMetricsFrameworkTileCaches();
    setDataRevision(rev => rev + 1);
  }, []);

  const filters: Filters | null = useMemo(() => {
    if (!dealerId) return null;
    return { dealerId, period };
  }, [dealerId, period]);

  const value = useMemo(
    () => ({
      dealerId,
      dealerTitle,
      setDealerContext,
      period,
      setPeriod,
      activeView,
      setActiveView,
      filters,
      mockApiFlag,
      setMockApiFlag,
      dataRevision,
    }),
    [
      dealerId,
      dealerTitle,
      period,
      activeView,
      filters,
      mockApiFlag,
      setMockApiFlag,
      dataRevision,
    ],
  );

  return (
    <MetricsFrameworkContext.Provider value={value}>{children}</MetricsFrameworkContext.Provider>
  );
};

export const useMetricsFrameworkContext = (): MetricsFrameworkContextType => {
  const ctx = useContext(MetricsFrameworkContext);
  if (!ctx) {
    throw new Error('useMetricsFrameworkContext must be used within MetricsFrameworkProvider');
  }
  return ctx;
};
